import React, { useState, useEffect } from "react";
import { Plus, Search, Edit, Trash2, Droplets, Calendar, User, CheckCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import api from "../utils/api";
import { toast } from "sonner";

const AREAS = [
  { value: "horno", label: "Horno" },
  { value: "mesa_trabajo", label: "Mesa de trabajo" },
  { value: "utensilios", label: "Utensilios" },
  { value: "cochera", label: "Cochera / Area de produccion" },
  { value: "almacen", label: "Almacen" },
  { value: "empaque", label: "Area de empaque" },
  { value: "otro", label: "Otro" },
];

function CleaningForm({ record, onClose, onSave }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    time: new Date().toTimeString().slice(0, 5),
    area: "", area_detail: "", cleaning_product: "", responsible: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record) setForm({
      date: record.date || "", time: record.time || "",
      area: record.area || "", area_detail: record.area_detail || "",
      cleaning_product: record.cleaning_product || "",
      responsible: record.responsible || "", notes: record.notes || "",
    });
  }, [record]);

  const handleSave = async () => {
    if (!form.area) { toast.error("Selecciona un area"); return; }
    if (!form.cleaning_product) { toast.error("Indica el producto de limpieza"); return; }
    if (!form.responsible) { toast.error("Indica el responsable"); return; }
    setSaving(true);
    try {
      if (record) { await api.put(`/cleaning-logs/${record.id}`, form); toast.success("Registro actualizado"); }
      else { await api.post("/cleaning-logs", form); toast.success("Registro de limpieza creado"); }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="cleaning-form">
        <DialogHeader><DialogTitle>{record ? "Editar Registro" : "Nuevo Registro de Limpieza"}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Fecha *</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="clean-date" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Hora *</label>
              <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="clean-time" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Area *</label>
            <select value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="clean-area">
              <option value="">Seleccionar area...</option>
              {AREAS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          {form.area === "otro" && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Especificar area</label>
              <input value={form.area_detail} onChange={e => setForm(f => ({ ...f, area_detail: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Describe el area..." />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Producto de limpieza *</label>
            <input value={form.cleaning_product} onChange={e => setForm(f => ({ ...f, cleaning_product: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Ej: Cloro, jabon biodegradable..." data-testid="clean-product" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Responsable *</label>
            <input value={form.responsible} onChange={e => setForm(f => ({ ...f, responsible: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Nombre del responsable" data-testid="clean-responsible" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Observaciones</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" rows={2} placeholder="Notas adicionales..." />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-cleaning-btn"
              className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium disabled:opacity-60">
              {saving ? "Guardando..." : record ? "Actualizar" : "Registrar"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Limpieza() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [filterArea, setFilterArea] = useState("");

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/cleaning-logs"); setLogs(r.data); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const deleteLog = async (id) => {
    if (!window.confirm("Eliminar este registro?")) return;
    try { await api.delete(`/cleaning-logs/${id}`); toast.success("Eliminado"); load(); }
    catch { toast.error("Error al eliminar"); }
  };

  const filtered = logs.filter(l => {
    if (filterArea && l.area !== filterArea) return false;
    if (search) {
      const q = search.toLowerCase();
      return (l.responsible || "").toLowerCase().includes(q) ||
        (l.cleaning_product || "").toLowerCase().includes(q) ||
        (l.folio || "").toLowerCase().includes(q);
    }
    return true;
  });

  const areaLabel = (v) => AREAS.find(a => a.value === v)?.label || v;

  return (
    <div className="space-y-4" data-testid="limpieza-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Registro de Limpieza</h1>
          <p className="text-xs text-slate-500">Control de limpieza SENASICA</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          data-testid="new-cleaning-btn">
          <Plus size={14} /> Nuevo Registro
        </button>
      </div>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por responsable, producto..."
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="cleaning-search" />
        </div>
        <select value={filterArea} onChange={e => setFilterArea(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="cleaning-area-filter">
          <option value="">Todas las areas</option>
          {AREAS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Cargando...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Droplets size={36} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 text-sm">No hay registros de limpieza</p>
          <p className="text-slate-400 text-xs">Crea el primero para cumplir con SENASICA</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(l => (
            <div key={l.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow"
              data-testid={`cleaning-row-${l.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-slate-400">{l.folio}</span>
                    <span className="px-2 py-0.5 bg-cyan-50 text-cyan-700 rounded text-[10px] font-medium">{areaLabel(l.area)}</span>
                    <CheckCircle size={12} className="text-green-500" />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-600">
                    <span className="flex items-center gap-1"><Calendar size={11} /> {l.date} {l.time}</span>
                    <span className="flex items-center gap-1"><User size={11} /> {l.responsible}</span>
                    <span className="flex items-center gap-1"><Droplets size={11} /> {l.cleaning_product}</span>
                  </div>
                  {l.notes && <p className="text-[10px] text-slate-400 mt-1">{l.notes}</p>}
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <button onClick={() => { setEditing(l); setShowForm(true); }}
                    className="p-1.5 hover:bg-slate-100 rounded" data-testid={`edit-cleaning-${l.id}`}>
                    <Edit size={13} className="text-slate-400" />
                  </button>
                  <button onClick={() => deleteLog(l.id)}
                    className="p-1.5 hover:bg-red-50 rounded" data-testid={`delete-cleaning-${l.id}`}>
                    <Trash2 size={13} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <CleaningForm record={editing} onClose={() => setShowForm(false)}
        onSave={() => { setShowForm(false); load(); }} />}
    </div>
  );
}
